import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import {
  AUTO_SIGNIN_INTERVAL_MS,
  MAX_RECOVERY_ATTEMPTS,
  RECOVERY_TIMEOUT_MS,
  autoSigninDelay,
  isSessionRemembered,
  recoveryAttemptCount,
  recordAutoSignin,
  recordRecoveryAttempt,
  requiresSignin,
  stopSigninRecovery,
} from "./session";

export type AutomaticSigninStatus =
  | "off"
  | "waiting"
  | "retrying"
  | "offline"
  | "signing-in"
  | "failed";

/**
 * Signs a remembered browser in again without anyone at the keyboard.
 *
 * It first renews with the stored refresh token, which needs no page change,
 * then falls back to a redirect to the identity provider, which returns at once
 * while the provider's own session is valid. Attempts are spaced by
 * `AUTO_SIGNIN_INTERVAL_MS` and wait for the network. Three unsuccessful
 * recovery attempts or a stalled operation require explicit user recovery;
 * the attempt budget survives page reloads and identity-provider redirects.
 */
export function useAutomaticSignin(returnTo: string | undefined): AutomaticSigninStatus {
  const auth = useAuth();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [running, setRunning] = useState(false);
  const [round, setRound] = useState(0);
  const [attempts, setAttempts] = useState(recoveryAttemptCount);
  const [timedOut, setTimedOut] = useState(false);
  const stopped = useRef(false);
  const [renewalRetryAt, setRenewalRetryAt] = useState(0);
  const [awaitingRenewalError, setAwaitingRenewalError] = useState(false);
  const [redirectRequired, setRedirectRequired] = useState(false);
  const remembered = isSessionRemembered();
  const signedIn = auth.isAuthenticated && !auth.user?.expired;
  const enabled = remembered && !timedOut && !auth.isLoading && !signedIn && !auth.activeNavigator;
  const signingOut =
    auth.activeNavigator === "signoutRedirect" || auth.activeNavigator === "signoutSilent";
  const pending =
    !signedIn &&
    !signingOut &&
    (running || awaitingRenewalError || auth.isLoading || Boolean(auth.activeNavigator));

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => {
      stopped.current = true;
      stopSigninRecovery();
      setTimedOut(true);
    }, RECOVERY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const { signinSilent, signinRedirect } = auth;
  const refreshToken = auth.user?.refresh_token;
  const loginHint = auth.user?.profile.preferred_username ?? auth.user?.profile.email ?? undefined;
  const signinDelay =
    refreshToken && !redirectRequired
      ? Math.max(0, renewalRetryAt - Date.now())
      : autoSigninDelay();

  useEffect(() => {
    if (!awaitingRenewalError || !enabled || !auth.error) return;
    setAwaitingRenewalError(false);
    if (requiresSignin(auth.error)) setRedirectRequired(true);
    else setRound((value) => value + 1);
  }, [awaitingRenewalError, enabled, auth.error]);

  useEffect(() => {
    if (!enabled || !online || running || awaitingRenewalError || attempts >= MAX_RECOVERY_ATTEMPTS)
      return;
    const timer = window.setTimeout(async () => {
      setRunning(true);
      setAttempts(recordRecoveryAttempt());
      try {
        if (refreshToken && !redirectRequired) {
          setRenewalRetryAt(Date.now() + AUTO_SIGNIN_INTERVAL_MS);
          try {
            const user = await signinSilent();
            if (!user) setAwaitingRenewalError(true);
            return;
          } catch (error) {
            if (!requiresSignin(error)) throw error;
          }
        }
        if (stopped.current) return;
        setRedirectRequired(true);
        if (autoSigninDelay() > 0) return;
        recordAutoSignin();
        await signinRedirect({ state: { returnTo }, login_hint: loginHint });
      } catch {
        // Try again after the interval.
        setRound((value) => value + 1);
      } finally {
        setRunning(false);
      }
    }, signinDelay);
    return () => window.clearTimeout(timer);
  }, [
    enabled,
    online,
    running,
    awaitingRenewalError,
    redirectRequired,
    round,
    attempts,
    refreshToken,
    loginHint,
    returnTo,
    signinDelay,
    signinSilent,
    signinRedirect,
  ]);

  if (signedIn || signingOut) return "off";
  if (timedOut) return "failed";
  if (!remembered) return "off";
  if (
    attempts >= MAX_RECOVERY_ATTEMPTS &&
    !running &&
    !auth.isLoading &&
    !auth.activeNavigator &&
    !awaitingRenewalError
  )
    return "failed";
  if (
    running ||
    auth.isLoading ||
    auth.activeNavigator ||
    awaitingRenewalError ||
    (redirectRequired && signinDelay === 0)
  )
    return "signing-in";
  if (!online) return "offline";
  return signinDelay > 0 ? "retrying" : "waiting";
}
